// The loop as one picture, for the admin panel's "How it works" page: what
// each phase reads (with the server-level connections), four phase boxes with
// the steps and the numbered prompt each one uses, and the return path
// through the playbook. Same drawing as the platform's How it works page.

import React, { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import api from '../../services/api';
import { USER_BACKEND_URL } from '../../config/config';
import { LOOP_PHASES, LOOP_PROMPTS, pNo } from './loopPromptMeta';

type ReadyKey = 'reddit' | 'serpapi' | 'youtube' | 'atp' | 'company';
interface Source { label: string; ready?: ReadyKey }

/* The external platforms each phase reads. `company` = connected per company
   in Social Accounts, so the admin cannot say on or off. */
const PHASE_SOURCES: Source[][] = [
  [
    { label: 'Reddit', ready: 'reddit' }, { label: 'Google PAA', ready: 'serpapi' }, { label: 'Answer the Public', ready: 'atp' },
    { label: 'YouTube comments', ready: 'youtube' }, { label: 'Sales calls' }, { label: 'X search', ready: 'company' },
    { label: 'X watch list', ready: 'company' }, { label: 'Reddit this week', ready: 'reddit' }, { label: 'LinkedIn (web)', ready: 'serpapi' },
    { label: 'Competitor sites' }, { label: 'Competitor YouTube', ready: 'youtube' }, { label: 'Competitor X', ready: 'company' },
  ],
  [{ label: 'Google / Outlook' }, { label: 'Transcript' }],
  [{ label: 'X account', ready: 'company' }, { label: 'LinkedIn account', ready: 'company' }, { label: 'X mentions', ready: 'company' }],
  [{ label: 'X metrics', ready: 'company' }, { label: 'LinkedIn metrics', ready: 'company' }, { label: 'YouTube metrics', ready: 'youtube' }, { label: 'Landing page' }],
];

const LoopDiagram: React.FC = () => {
  const theme = useTheme();
  const [ready, setReady] = useState<Partial<Record<ReadyKey, boolean>>>({});
  useEffect(() => {
    let alive = true;
    api.get(`${USER_BACKEND_URL}loop/config`)
      .then(r => { const sr = r?.data?.data?.sources_ready; if (alive && sr) setReady({ reddit: !!sr.reddit, serpapi: !!sr.serpapi, youtube: !!sr.youtube_key, atp: !!sr.atp }); })
      .catch(() => { /* unknown: chips stay neutral */ });
    return () => { alive = false; };
  }, []);

  const C = {
    ink: theme.palette.text.primary, muted: theme.palette.text.secondary, line: theme.palette.divider,
    surface: theme.palette.background.paper, sunk: theme.palette.action.hover,
    accent: theme.palette.primary.main, accentSoft: theme.palette.primary.light, good: theme.palette.success.main,
  };
  const byNumber = Object.fromEntries(LOOP_PROMPTS.map(p => [p.number, p]));

  const BOX_W = 262, GAP = 36, X0 = 16, ROW = 50, BOX_H = 72 + 5 * ROW + 6;
  const CHIP_W = 124, CHIP_H = 20, CHIP_GAP = 5, BAND_ROWS = 6;
  const BAND_Y = 30, BAND_H = BAND_ROWS * (CHIP_H + CHIP_GAP), Y0 = BAND_Y + BAND_H + 34;
  const W = X0 * 2 + BOX_W * 4 + GAP * 3, H = Y0 + BOX_H + 96;
  const boxX = (i: number) => X0 + i * (BOX_W + GAP);
  const dotFor = (src: Source) => {
    if (src.ready === undefined || src.ready === 'company') return C.accent;
    const r = ready[src.ready];
    return r === undefined ? C.muted : r ? C.good : C.muted;
  };
  const isOff = (src: Source) => src.ready !== undefined && src.ready !== 'company' && ready[src.ready] === false;

  return (
    <Box component="figure" sx={{ m: 0, overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="The learning loop: Listen, Record, Publish and talk, Measure and learn, with the playbook and the next brief feeding back into Listen." style={{ display: 'block', width: '100%', minWidth: 900, height: 'auto' }} fontFamily="system-ui, sans-serif" fontSize="12">
        <defs>
          <marker id="adm-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0 L10 5 L0 10 z" fill={C.muted} /></marker>
          <marker id="adm-arr-acc" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 0 L10 5 L0 10 z" fill={C.accent} /></marker>
        </defs>
        <text x={X0} y={BAND_Y - 12} fontSize="10.5" fontWeight="600" fill={C.muted} letterSpacing=".06em">WHAT EACH PHASE READS · GREEN = CONNECTED ON THE SERVER, GREY = NOT CONNECTED, BLUE = INSIDE THE SYSTEM OR PER COMPANY</text>
        {/* legend */}
        {(() => {
          const items = [{ g: '✎', c: C.good, l: 'Prompt in Braintrust, editable here' }, { g: '⚙', c: C.muted, l: 'Rules, no prompt' }];
          const lw = 220, ly = Y0 - 16;
          return (
            <g>
              <rect x={W - X0 - items.length * lw - 12} y={ly - 15} width={items.length * lw + 12} height="24" rx="12" fill={C.sunk} stroke={C.line} />
              {items.map((it, k) => (
                <text key={k} x={W - X0 - (items.length - k) * lw} y={ly + 1} fontSize="11" fill={C.ink}><tspan fill={it.c} fontWeight="700">{it.g}</tspan> {it.l}</text>
              ))}
            </g>
          );
        })()}
        {LOOP_PHASES.map((phase, i) => {
          const x = boxX(i);
          const srcs = PHASE_SOURCES[i].slice(0, BAND_ROWS * 2);
          return (
            <g key={phase.title}>
              {srcs.map((src, k) => {
                const cx = x + (k % 2) * (CHIP_W + 10), cy = BAND_Y + Math.floor(k / 2) * (CHIP_H + CHIP_GAP);
                const off = isOff(src);
                return (
                  <g key={src.label} opacity={off ? 0.7 : 1}>
                    <title>{src.label}{off ? ' — not connected on the server' : src.ready === 'company' ? ' — connected per company in Social Accounts' : ''}</title>
                    <rect x={cx} y={cy} width={CHIP_W} height={CHIP_H} rx="10" fill={C.sunk} stroke={C.line} strokeDasharray={off ? '3 3' : undefined} />
                    <circle cx={cx + 11} cy={cy + CHIP_H / 2} r="4" fill={dotFor(src)} />
                    <text x={cx + 20} y={cy + CHIP_H / 2 + 4} fontSize="11" fill={C.ink}>{src.label}</text>
                  </g>
                );
              })}
              <line x1={x + BOX_W / 2} y1={BAND_Y + BAND_H + 2} x2={x + BOX_W / 2} y2={Y0 - 3} stroke={C.line} strokeWidth="1.5" markerEnd="url(#adm-arr)" />
              <rect x={x} y={Y0} width={BOX_W} height={BOX_H} rx="10" fill={C.surface} stroke={C.line} strokeWidth="1.5" />
              <circle cx={x + 22} cy={Y0 + 24} r="11" fill={C.accentSoft} opacity="0.35" />
              <text x={x + 22} y={Y0 + 28} textAnchor="middle" fontSize="11" fontWeight="700" fill={C.accent} fontFamily="monospace">{i + 1}</text>
              <text x={x + 42} y={Y0 + 29} fontSize="15" fontWeight="700" fill={C.ink}>{phase.title}</text>
              <line x1={x + 14} y1={Y0 + 46} x2={x + BOX_W - 14} y2={Y0 + 46} stroke={C.line} />
              {phase.steps.map((step, j) => {
                const y = Y0 + 70 + j * ROW;
                const refs = step.prompts.map(n => byNumber[n]).filter(Boolean);
                const shown = refs.slice(0, 2), more = refs.length - shown.length;
                return (
                  <g key={step.title}>
                    <title>{refs.length ? refs.map(r => `${pNo(r.number)} ${r.name} — ${r.slug}`).join('\n') : 'Rules, no prompt'}</title>
                    <text x={x + 14} y={y + 4} fontSize="13" fontWeight="600" fill={C.ink}>{step.title}</text>
                    {refs.length === 0 ? (
                      <text x={x + 14} y={y + 19} fontSize="10.5" fill={C.muted}><tspan fontWeight="700">⚙</tspan> Rules, no prompt</text>
                    ) : (
                      <text x={x + 14} y={y + 19} fontSize="10.5">
                        <tspan fill={C.muted} fontSize="9" fontWeight="700" letterSpacing=".06em">PROMPT </tspan>
                        {shown.map((r, k) => (
                          <tspan key={r.slug} fill={C.good} fontWeight="600">{k > 0 ? ' · ' : ''}<tspan fontFamily="monospace">{pNo(r.number)}</tspan> ✎ {r.name}</tspan>
                        ))}
                        {more > 0 && <tspan fill={C.muted}> +{more}</tspan>}
                      </text>
                    )}
                    <text x={x + 14} y={y + 33} fontSize="10" fill={C.muted} fontFamily="monospace">{step.when}</text>
                    {refs.map((r, k) => <circle key={k} cx={x + BOX_W - 18 - k * 13} cy={y} r="5" fill={C.good} />)}
                    {refs.length === 0 && <circle cx={x + BOX_W - 18} cy={y} r="5" fill={C.muted} />}
                  </g>
                );
              })}
              {i < LOOP_PHASES.length - 1 && (
                <line x1={x + BOX_W + 2} y1={Y0 + BOX_H / 2} x2={x + BOX_W + GAP - 3} y2={Y0 + BOX_H / 2} stroke={C.muted} strokeWidth="1.5" markerEnd="url(#adm-arr)" />
              )}
            </g>
          );
        })}
        {(() => {
          const yBottom = Y0 + BOX_H, yPath = yBottom + 52, xStart = boxX(3) + BOX_W / 2, xEnd = boxX(0) + BOX_W / 2;
          const pillW = 300, pillX = (W - pillW) / 2;
          return (
            <g>
              <path d={`M${xStart} ${yBottom} V${yPath} H${pillX + pillW + 4}`} fill="none" stroke={C.accent} strokeWidth="1.8" />
              <path d={`M${pillX - 4} ${yPath} H${xEnd} V${yBottom + 4}`} fill="none" stroke={C.accent} strokeWidth="1.8" markerEnd="url(#adm-arr-acc)" />
              <rect x={pillX} y={yPath - 18} width={pillW} height="36" rx="18" fill={C.surface} stroke={C.accent} strokeWidth="1.5" />
              <text x={W / 2} y={yPath - 2} textAnchor="middle" fontSize="13" fontWeight="700" fill={C.accent}>Playbook + next brief</text>
              <text x={W / 2} y={yPath + 12} textAnchor="middle" fontSize="10.5" fill={C.accent}>best hook · hot and dead topics · best slots · rules · competitor shapes</text>
              <text x={xStart + 10} y={yBottom + 30} fontSize="11" fill={C.muted}>what worked</text>
              <text x={xEnd + 10} y={yBottom + 30} fontSize="11" fill={C.muted}>next direction</text>
            </g>
          );
        })()}
      </svg>
      <Typography component="figcaption" variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Each green dot is one prompt in Braintrust, editable from this page. The green path at the bottom is what makes it a loop: what worked rewrites the playbook, and the next brief and the next drafts read it.
      </Typography>
    </Box>
  );
};

export default LoopDiagram;
